import React from 'react';
import Box from '../uikit/box/Box';
import Text from '../uikit/text/Text';
import RatingDot from '../shared/rating/RatingDot';
import { getRatingLabel } from '../../lib/utils';
import { ClampedText } from '../shared/Collapsible';

/**
 * The seven-dimension breakdown behind a meeting's AI rating (spec §8.1 detail
 * view).
 *
 * Built from the existing rating vocabulary — the 1-5 Poor..Superior labels,
 * `colors.ratings`, and RatingDot — rather than a charting library, which the
 * Webpack build has no business pulling in for seven bars.
 *
 * Two rules from the spec are visible here:
 *   - a first meeting has no continuity score; its composite comes from the
 *     other six (the API rescales their weights), and continuity shows as
 *     not applicable rather than looking like a failure.
 *   - weights are never sent to the client, so nothing here can display them.
 */

// Human-readable names for the rubric keys. Falls back to de-slugging an
// unknown key so a future rubric version still renders.
const LABELS = {
  objective_achievement: 'Objective achievement',
  action_clarity: 'Action clarity',
  decision_resolution: 'Decision resolution',
  continuity_followthrough: 'Continuity & follow-through',
  constructive_engagement: 'Constructive engagement',
  balanced_participation: 'Balanced participation',
  focus_time_discipline: 'Focus & time discipline',
};

const labelFor = (key) =>
  LABELS[key] || String(key).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** Score as a proportional bar, coloured from the shared ratings palette. */
const ScoreBar = ({ score }) => (
  <Box
    bg="grays.0"
    height="6px"
    width="100%"
    mt={1}
    style={{ borderRadius: '3px', overflow: 'hidden' }}
  >
    <Box
      height="6px"
      width={`${(Number(score) / 5) * 100}%`}
      bg={`ratings.${Math.max(0, Math.min(4, Math.round(Number(score)) - 1))}`}
    />
  </Box>
);

const Dimension = ({ dimension }) => {
  const scored = dimension.score !== null && dimension.score !== undefined;

  return (
    <Box py={3} borderBottom="1px solid" borderColor="grays.1">
      <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
        {scored && <RatingDot rating={dimension.score} />}
        <Text fontFamily="montserrat" fontSize={1} fontWeight="bold" flex="1">
          {labelFor(dimension.key)}
        </Text>
        <Text fontFamily="montserrat" fontSize={1} color={scored ? 'primarys.0' : 'grays.2'}>
          {scored ? `${dimension.score} · ${getRatingLabel(dimension.score)}` : 'Not applicable'}
        </Text>
      </Box>

      {scored && <ScoreBar score={dimension.score} />}

      {dimension.justification ? (
        // Justifications are one line by contract, but a model is not bound by
        // that, so they clamp like the summary does.
        <ClampedText lines={2} mt={2}>
          {dimension.justification}
        </ClampedText>
      ) : (
        <Text fontFamily="montserrat" fontSize={0} color="grays.2" mt={2}>
          Not scored for this meeting.
        </Text>
      )}
    </Box>
  );
};

const DimensionBreakdown = ({ intelligence }) => {
  if (!intelligence || !intelligence.dimensions || intelligence.dimensions.length === 0) {
    return null;
  }

  const { ai_composite, band, composite_unavailable_reason, dimensions } = intelligence;

  return (
    <Box>
      <Box display="flex" alignItems="center" py={3} style={{ gap: '10px' }}>
        {ai_composite !== null && ai_composite !== undefined ? (
          <React.Fragment>
            <RatingDot rating={ai_composite} size={20} />
            <Text fontFamily="montserrat" fontSize={4} fontWeight="bold">
              {ai_composite}
            </Text>
            {band && (
              <Text fontFamily="montserrat" fontSize={1} color="secondarys.0">
                {band}
              </Text>
            )}
          </React.Fragment>
        ) : (
          <Text fontFamily="montserrat" fontSize={1} color="grays.2">
            {composite_unavailable_reason || 'No overall rating for this meeting.'}
          </Text>
        )}
      </Box>

      {dimensions.map((d) => (
        <Dimension key={d.key} dimension={d} />
      ))}

      {(intelligence.model_id || intelligence.weight_profile) && (
        <Text fontFamily="montserrat" fontSize={0} color="grays.2" mt={3}>
          {[
            intelligence.weight_profile && `Priorities: ${intelligence.weight_profile}`,
            intelligence.meeting_type && `Type: ${String(intelligence.meeting_type).replace(/_/g, ' ')}`,
            intelligence.model_id,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      )}
    </Box>
  );
};

export default DimensionBreakdown;
